import { Box } from "@mui/material";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
} from "src/domains/shared/constants/styles/site";
import { scrollToElement } from "../siteKit/motion/reducedMotion";
import { useScrollFrame } from "../siteKit/motion/scrollFrame";
import { SITE_SECTION_ATTR } from "../siteKit/SiteSection";
import { STORY_HERO_ATTR } from "./StoryHero";

/** 구역 눈금이 셀 구역 — 첫 장면과 사이트 구역 */
const SECTION_SELECTOR = `[${STORY_HERO_ATTR}], [${SITE_SECTION_ATTR}]`;

const RAIL_STYLES = {
  rail: {
    position: "fixed",
    left: "clamp(14px,2vw,28px)",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 70,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "10px",
    "& a": {
      display: "block",
      width: "14px",
      height: "2px",
      borderRadius: "1px",
      background: "rgba(28,31,37,.26)",
      transition: "width .3s,background .3s",
    },
    "& a:hover": { background: "rgba(28,31,37,.6)" },
    "& a[data-on]": { width: "24px", background: "#1C1F25" },
    "&[data-light] a": {
      background: "rgba(255,255,255,.5)",
      boxShadow: "0 0 6px rgba(6,10,16,.25)",
    },
    "&[data-light] a[data-on]": { background: "#fff" },
    [SITE_MEDIA.phone]: { display: "none" },
    // 손가락 화면: 2px 막대는 누를 수 없고, 44px 로 넓히면 일곱 개가 가로 휴대폰 화면 높이를 넘는다 — 쓸어 넘기기로 충분하다
    "@media (pointer: coarse)": { display: "none" },
  },
} as const;

/**
 * 토스 홈식 구역 눈금(marketing-landing): 왼쪽 가운데에 구역마다 짧은 막대, 지금 구역은 길고 진하게, 첫 장면 위에서는 흰색.
 * 누르면 그 구역으로. 지금 구역·밝기는 스크롤 프레임마다 DOM 속성으로만 바꾼다.
 */
export function SectionRail({
  containerRef,
}: {
  containerRef: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("landing");
  const railRef = useRef<HTMLElement | null>(null);
  const sectionsRef = useRef<HTMLElement[]>([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    sectionsRef.current = [
      ...(containerRef.current?.querySelectorAll<HTMLElement>(
        SECTION_SELECTOR
      ) ?? []),
    ];
    setCount(sectionsRef.current.length);
  }, [containerRef]);

  useScrollFrame((_, viewportHeight) => {
    const rail = railRef.current;
    const sections = sectionsRef.current;
    if (!rail || !sections.length) return;
    const middle = viewportHeight * 0.5;
    let current = 0;
    sections.forEach((section, index) => {
      if (section.getBoundingClientRect().top - SITE_HEADER_HEIGHT_PX <= middle)
        current = index;
    });
    [...rail.children].forEach((link, index) =>
      link.toggleAttribute("data-on", index === current)
    );
    rail.toggleAttribute(
      "data-light",
      sections[0].getBoundingClientRect().bottom > middle
    );
  });

  return (
    <Box
      component="nav"
      ref={railRef}
      aria-label={t("site.sectionIndex")}
      sx={RAIL_STYLES.rail}
    >
      {Array.from({ length: count }, (_, index) => (
        <a
          key={index}
          href="#"
          aria-label={t("site.sectionIndexItem", { n: index + 1 })}
          onClick={(event) => {
            event.preventDefault();
            const section = sectionsRef.current[index];
            if (section) scrollToElement(section);
          }}
        />
      ))}
    </Box>
  );
}
